import { z } from 'zod';
import type { IntentEnvelopeV1, IntentOperation } from './intent-envelope.js';
import { normalizeSourceFieldValue } from '../broker/field-value-normalizer.js';
import { containsWholeToken } from './planner-first-reads.js';
import {
  plannerWriteMissingFieldsAllowed,
  plannerWriteMissingInformation,
} from './planner-first-write-required.js';
const text = (max: number) => z.string().trim().min(1).max(max);
const fact = (max: number) => z.object({ rawValue: text(max), sourceQuote: text(4000) }).strict();
const target = z.union([
  z.object({ entity: z.enum(['deal', 'contact']), name: fact(200) }).strict(),
  z.object({ entity: z.enum(['deal', 'contact']), source: z.literal('trusted_context') }).strict(),
]);
const tagTarget = z.union([
  z.object({ entity: z.enum(['deal', 'contact', 'company']), name: fact(200) }).strict(),
  z
    .object({
      entity: z.enum(['deal', 'contact', 'company']),
      source: z.literal('trusted_context'),
    })
    .strict(),
]);
export const plannerContactNoteStageSchema = z.discriminatedUnion('capability', [
  z
    .object({
      capability: z.literal('create_contact'),
      sourceQuote: text(4000),
      firstName: fact(100).optional(),
      lastName: fact(100).optional(),
      phone: fact(30).optional(),
      email: fact(320).optional(),
      source: fact(100).optional(),
      tags: z.array(fact(50)).min(1).max(10).optional(),
    })
    .strict(),
  z
    .object({
      capability: z.literal('update_contact'),
      sourceQuote: text(4000),
      target: target.refine((value) => value.entity === 'contact').optional(),
      phone: fact(30).optional(),
      email: fact(320).optional(),
      firstName: fact(100).optional(),
      lastName: fact(100).optional(),
    })
    .strict(),
  z
    .object({
      capability: z.literal('update_deal'),
      sourceQuote: text(4000),
      target: target.refine((value) => value.entity === 'deal').optional(),
      title: fact(200).optional(),
      amount: fact(100).optional(),
    })
    .strict(),
  z
    .object({
      capability: z.literal('add_tags'),
      sourceQuote: text(4000),
      target: tagTarget.optional(),
      tags: z.array(fact(50)).min(1).max(10).optional(),
    })
    .strict(),
  z
    .object({
      capability: z.literal('add_note'),
      sourceQuote: text(4000),
      body: fact(4000).optional(),
      target: target.optional(),
    })
    .strict(),
  z
    .object({
      capability: z.literal('change_deal_stage'),
      sourceQuote: text(4000),
      target: target.refine((value) => value.entity === 'deal').optional(),
      stage: fact(100).optional(),
      reason: fact(200).optional(),
    })
    .strict(),
]);
export type ContactNoteStageWrite = z.infer<typeof plannerContactNoteStageSchema>;
export function isContactNoteStageWrite(write: {
  capability: string;
}): write is ContactNoteStageWrite {
  return [
    'create_contact',
    'update_contact',
    'update_deal',
    'add_tags',
    'add_note',
    'change_deal_stage',
  ].includes(write.capability);
}
export function contactNoteStageSourceValid(write: ContactNoteStageWrite, source: string): boolean {
  if (!source.includes(write.sourceQuote)) return false;
  const valid = (node: unknown): boolean => {
    if (!node || typeof node !== 'object') return true;
    if ('rawValue' in node && 'sourceQuote' in node) {
      const value = node as { rawValue: string; sourceQuote: string };
      return (
        write.sourceQuote.includes(value.sourceQuote) && value.sourceQuote.includes(value.rawValue)
      );
    }
    return Object.values(node).every(valid);
  };
  if (!valid(write)) return false;
  return (
    write.capability !== 'change_deal_stage' ||
    !write.stage ||
    (containsWholeToken(write.sourceQuote, write.stage.rawValue) &&
      containsWholeToken(write.stage.sourceQuote, write.stage.rawValue) &&
      stageValue(write.stage.rawValue) !== undefined)
  );
}
export const stageSchema = z.enum(['New', 'Contacted', 'Negotiation', 'Won', 'Lost']);
export function stageValue(raw: string): z.infer<typeof stageSchema> | undefined {
  const result = normalizeSourceFieldValue(raw, stageSchema);
  return result.ok ? stageSchema.parse(result.value) : undefined;
}
export function contactNoteStageEnvelope(write: ContactNoteStageWrite): IntentEnvelopeV1 {
  const fields: IntentOperation['fields'] = [];
  const missingInformation = plannerWriteMissingInformation(write, true);
  const relations: IntentOperation['relations'] = [];
  const ref =
    'target' in write && write.target
      ? 'name' in write.target
        ? {
            entity: write.target.entity,
            displayName: write.target.name.rawValue,
            sourceQuote: write.target.name.sourceQuote,
          }
        : { entity: write.target.entity, source: 'trusted_context' as const }
      : null;
  if (write.capability === 'create_contact') {
    for (const key of ['firstName', 'lastName', 'phone', 'email', 'source'] as const)
      if (write[key]) fields.push({ field: key, ...write[key]! });
    if (write.tags?.length)
      fields.push({
        field: 'tags',
        rawValue: write.tags.map((tag) => tag.rawValue.replace(/^#/u, '')),
        sourceQuote: write.sourceQuote,
      });
  } else if (write.capability === 'add_tags') {
    if (write.tags?.length)
      fields.push({
        field: 'tags',
        rawValue: write.tags.map((tag) => tag.rawValue.replace(/^#/u, '')),
        sourceQuote: write.sourceQuote,
      });
  } else if (write.capability === 'update_contact') {
    for (const key of ['phone', 'email', 'firstName', 'lastName'] as const)
      if (write[key]) fields.push({ field: key, ...write[key]! });
  } else if (write.capability === 'update_deal') {
    for (const key of ['title', 'amount'] as const)
      if (write[key]) fields.push({ field: key, ...write[key]! });
  } else {
    if (write.capability === 'add_note') {
      if (write.body) fields.push({ field: 'body', ...write.body });
      if (ref) relations.push({ field: 'links', target: ref, sourceQuote: write.sourceQuote });
    } else {
      if (write.stage) fields.push({ field: 'toStage', ...write.stage });
      if (write.reason) fields.push({ field: 'reason', ...write.reason });
    }
  }
  return {
    version: 1,
    mode: 'crm_write',
    confidence: 'high',
    missingInformation,
    unsupportedRequirements: [],
    operations: [
      {
        clientOperationId: 'planner-first-write',
        entity:
          write.capability === 'add_tags'
            ? (write.target?.entity ?? 'contact')
            : write.capability === 'create_contact' || write.capability === 'update_contact'
              ? 'contact'
              : write.capability === 'add_note'
                ? 'note'
                : 'deal',
        operation:
          write.capability === 'add_tags'
            ? 'tags'
            : write.capability === 'change_deal_stage'
              ? 'stage'
              : write.capability === 'update_contact' || write.capability === 'update_deal'
                ? 'update'
                : 'create',
        target:
          write.capability === 'add_tags' ||
          write.capability === 'change_deal_stage' ||
          write.capability === 'update_contact' ||
          write.capability === 'update_deal'
            ? ref
            : null,
        fields,
        relations,
        dependsOn: [],
        sourceQuote: write.sourceQuote,
      },
    ],
  };
}
export function contactNoteStageClarificationAllowed(
  write: ContactNoteStageWrite,
  missing: readonly { field: string }[],
): boolean {
  return plannerWriteMissingFieldsAllowed(write, missing);
}
/** Semantic field keys only; localized labels remain in the UI catalogs. */
export function plannerWriteMissingTargetField(field: string, capability: string): string {
  if (field === 'contact' && capability === 'create_contact') return 'contactId';
  if (field !== 'target.name') return field;
  if (capability === 'add_tags') return 'specialTarget';
  return capability === 'add_note'
    ? 'noteTarget'
    : capability === 'update_contact'
      ? 'contactId'
      : capability === 'change_deal_stage' || capability === 'update_deal'
        ? 'dealId'
        : 'taskId';
}
export const CONTACT_NOTE_STAGE_GUIDANCE = [
  'create_contact, add_note and change_deal_stage MUST use kind:write. Strict DTOs: create_contact {sourceQuote,firstName?,lastName?,phone?,email?,source?}; add_note {sourceQuote,body?,target?}; change_deal_stage {sourceQuote,target?,stage?,reason?}. Every supplied field is {rawValue,sourceQuote}. target is {entity:deal|contact,name:{rawValue,sourceQuote}} or {entity,source:trusted_context}; stage target must be deal. Never IDs, versions, links or opaque args.',
  'Missing firstName/body/target.name/stage requires named clarification. An orphan note must clarify the target, not fall back as unsupported. Extra fields, sends and multiple operations are unsupported in full. Trusted context requires a real current UI/context record; it never invents a target.',
  'Example: {"kind":"write","write":{"capability":"create_contact","sourceQuote":"Create contact Ada Finch","firstName":{"rawValue":"Ada","sourceQuote":"Ada"},"lastName":{"rawValue":"Finch","sourceQuote":"Finch"}}}.',
  'Example: {"kind":"write","write":{"capability":"add_note","sourceQuote":"Add note Budget approved to deal Cedar Renewal","body":{"rawValue":"Budget approved","sourceQuote":"Budget approved"},"target":{"entity":"deal","name":{"rawValue":"Cedar Renewal","sourceQuote":"Cedar Renewal"}}}}.',
  'Example: {"kind":"write","write":{"capability":"change_deal_stage","sourceQuote":"Move deal Cedar Renewal to Won","target":{"entity":"deal","name":{"rawValue":"Cedar Renewal","sourceQuote":"Cedar Renewal"}},"stage":{"rawValue":"Won","sourceQuote":"Won"}}}.',
].join('\n');

/** JSONB may reorder object keys; arrays and all literal facts remain exact. */
export function nativeEnvelopeMatches(envelope: unknown, write: ContactNoteStageWrite): boolean {
  const ordered = (value: unknown): unknown =>
    Array.isArray(value)
      ? value.map(ordered)
      : value !== null && typeof value === 'object'
        ? Object.fromEntries(
            Object.entries(value)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([key, item]) => [key, ordered(item)]),
          )
        : value;
  return (
    JSON.stringify(ordered(envelope)) === JSON.stringify(ordered(contactNoteStageEnvelope(write)))
  );
}
